import React, { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectInput from 'components/common/SelectInput';
import {
  NewIntegration,
  DestinationType,
  Organization,
} from 'components/common/ObjectTypes';
import { DraftReferenceState } from 'api/referenceOptions';
import { AttentionIcon } from 'components/common/ProjectIcons';
import {
  connectionSetupActions,
  destinationSetupActions,
} from 'components/common/SchemaFormTemplates/actionFilters';
import { baseUrlIsRequired } from 'components/routes/providerBaseUrl';
import { isEarthRangerDestinationType } from './earthRangerUrl';
import EarthRangerSiteInput from './EarthRangerSiteInput';
import TechnologyIcon from 'components/common/TechnologyIcon';
import TechnologyCardGrid, {
  TechnologyCardItem,
} from 'components/common/TechnologyCardGrid';
import TechnologySearchInput from 'components/common/TechnologySearchInput';
import NoResults from 'components/common/NoResults';
import { DraftTestActionButton } from 'components/common/DraftTestActionButton';
import {
  buttonClasses,
  textClasses,
  wizardClasses,
} from 'components/common/tailwindClassnames';
import { useHelp, HelpCard } from 'context/HelpContext';
import SelectionPanel from 'components/common/configPanel/SelectionPanel';
import ActionSectionForm from 'components/common/configPanel/ActionSectionForm';
import {
  DETAILS_SLUG,
  actionForSlug,
  attachRequiredCounts,
  buildActionNav,
  flagSectionErrors,
} from 'components/common/configPanel/buildActionNav';
import { actionDescription } from 'components/common/configPanel/actionSetup';
import ScheduleToggle from 'components/common/configPanel/ScheduleToggle';
import RequiredTag from 'components/common/RequiredTag';

type DestinationModalProps = {
  currentMemberIsAdmin: boolean;
  errors: any;
  rsjfErrors: any;
  onToggleModal: MouseEventHandler;
  destination: NewIntegration;
  destinationType: DestinationType;
  organizations: Organization[];
  loadMoreOrgs: Function;
  orgInputChange: Function;
  orgSearchActive?: boolean;
  destinationTypes: DestinationType[];
  // False while the technology list is still being fetched: the grid shows
  // skeleton cards rather than "no results".
  typesLoaded: boolean;
  // The fetch failed. Distinct from an empty list, which means the search
  // matched nothing.
  typesError?: boolean;
  // Back to the technology grid, discarding what was typed for the technology
  // being abandoned.
  onClearType: () => void;
  // True when this modal is creating a destination for a connection: only the
  // receiving capability is relevant, so the grid is flat and push-only.
  receiveOnly?: boolean;
  onChange: Function;
  onChangeOrg: Function;
  onChangeType: Function;
  onChangeJSONForm: Function;
  // The configurations typed so far, so a section that was navigated away from
  // comes back with its values instead of an empty form.
  configurations: any[];
  onSave: (
    e: React.FormEvent<Element>,
    action?: 'save' | 'saveAndClose',
  ) => void;
  onGoToDetails: (destination_id: string) => void;
  changesMade: boolean;
  draftReference?: DraftReferenceState;
  authToken?: string;
};

const DestinationModal = ({
  currentMemberIsAdmin,
  errors,
  rsjfErrors,
  onToggleModal,
  destination,
  destinationType,
  organizations,
  loadMoreOrgs,
  orgInputChange,
  orgSearchActive = false,
  destinationTypes,
  typesLoaded,
  typesError = false,
  onClearType,
  receiveOnly = false,
  onChange,
  onChangeOrg,
  onChangeType,
  onChangeJSONForm,
  configurations,
  onSave,
  onGoToDetails,
  changesMade,
  draftReference,
  authToken,
}: DestinationModalProps) => {
  const { t, i18n } = useTranslation();
  const { setHelpContent } = useHelp();
  // Every section is on the page at once (GUNDI-5697), so the nav follows the
  // scroll and the pointer rather than choosing what renders. This only tracks
  // the last section a nav click asked for.
  const [, setActiveSlug] = useState<string>(DETAILS_SLUG);
  // Filters the technology grid. Local: the whole list is already in hand, so
  // there is nothing to ask the server for.
  const [typeSearch, setTypeSearch] = useState<string>('');
  // Only a few technologies are reached at an address the user supplies; for
  // the rest it is an optional override, and it belongs in its own Endpoint
  // section rather than among the fields creation actually requires.
  const urlIsRequired = baseUrlIsRequired(destinationType.value);
  const isEarthRanger = isEarthRangerDestinationType(destinationType.name);

  // Track if a type has been selected
  const typeSelected =
    destinationType && destinationType.id && destinationType.id !== '0';
  // Choosing a card marks it; the technology is committed by the footer's
  // Next, not by the click — a click used to jump straight to the form, so a
  // misclick landed the user in a configuration they never asked for.
  const [pendingTypeId, setPendingTypeId] = useState<string | null>(null);
  const confirmPendingType = () => {
    const picked = destinationTypes.find(
      (type: any) => type.id === pendingTypeId,
    );
    if (picked) onChangeType(picked);
  };

  useEffect(() => {
    if (!typeSelected) {
      setHelpContent(null);
      return;
    }
    const cards: HelpCard[] = [
      {
        title: t('Setting up a Connection in Gundi'),
        description: t(
          'Find step-by-step instructions for connecting a system to Gundi, as a source or a destination.',
        ),
        button: {
          label: t('Learn More'),
          url: 'https://support.earthranger.com/connections-gundi/connections-in-gundi',
        },
      },
      ...(destinationType.help_center_url
        ? [
            {
              title: t('{{technology}} Destination Set Up', {
                technology: destinationType.name,
              }),
              description: t(
                'Configuration guide specific to {{technology}} destinations.',
                { technology: destinationType.name },
              ),
              button: {
                label: t('Learn More'),
                url: destinationType.help_center_url,
              },
            },
          ]
        : []),
    ];
    setHelpContent({ cards });
  }, [typeSelected, destinationType, i18n.language, t, setHelpContent]);

  useEffect(() => {
    return () => setHelpContent(null);
  }, [setHelpContent]);

  const hasAction = (type: DestinationType, kind: string) =>
    (type.actions || []).some((a: any) => a.type === kind);
  const toCard = (type: DestinationType): TechnologyCardItem => ({
    id: type.id,
    name: type.name,
    value: type.value,
    description: type.description,
  });
  // Same fields the Route wizard searches on (it sends
  // `search_fields: "name,value"`), so the two screens behave alike.
  const query = typeSearch.trim().toLowerCase();
  const searched = destinationTypes.filter(
    (type) =>
      (type.name || '').toLowerCase().includes(query) ||
      (type.value || '').toLowerCase().includes(query),
  );
  // A record's role comes from the Routes it joins, so every technology is
  // offered. The two groups say what each one is capable of; a technology that
  // can do both appears in both. Creating a destination for a Route, only one
  // capability is on offer — a heading that says nothing is worse than none, so
  // that grid is flat and push-only.
  const allGroups: { heading: string | null; items: TechnologyCardItem[] }[] =
    receiveOnly
      ? [
          {
            heading: null,
            items: searched
              .filter((type) => hasAction(type, 'push'))
              .map(toCard),
          },
        ]
      : [
          // A technology with neither direction still has to be selectable:
          // Generic Webhooks carries its configuration in the type's `webhook`
          // field rather than in an action, so it looks direction-less here and
          // was silently dropped. Those count as sending to Gundi.
          {
            heading: t('Can send data to Gundi'),
            items: searched
              .filter(
                (type) => hasAction(type, 'pull') || !hasAction(type, 'push'),
              )
              .map(toCard),
          },
          {
            heading: t('Can receive data from Gundi'),
            items: searched
              .filter((type) => hasAction(type, 'push'))
              .map(toCard),
          },
        ];
  const gridGroups = allGroups.filter((group) => group.items.length > 0);

  // Only actions the user can fill in become sections. Creating a destination
  // FOR a Route, the role is settled and the sending direction is irrelevant;
  // creating a Connection in its own right, no direction is — its role comes
  // from the Routes it joins, and a technology picked for what it can send
  // must show the section that sends.
  const actions = useMemo(
    () =>
      receiveOnly
        ? destinationSetupActions(destinationType.actions)
        : connectionSetupActions(destinationType.actions),
    [destinationType.actions, receiveOnly],
  );
  const dataFor = (action: any) =>
    configurations.find((c: any) => c.action === action.id)?.data ?? {};
  // Same nav as the Route wizard: a count of what a section still needs, going
  // red once a submit has failed on it.
  // Same count on the General section as the Route wizard shows on its details.
  const detailsRequiredCount = [
    !destination.name?.trim(),
    !destination.owner,
    ...(urlIsRequired ? [!destination.base_url?.trim()] : []),
  ].filter(Boolean).length;
  const nav = attachRequiredCounts(
    flagSectionErrors(
      buildActionNav({
        actions,
        t,
        detailsNeedsSetup: Object.keys(errors).length > 0,
        detailsRequiredCount,
      }),
      actions,
      rsjfErrors,
    ),
    actions,
    dataFor,
    rsjfErrors,
  );

  // One action's form, rendered inline with all the others.
  const actionSection = (action: any) =>
    !action ? null : (
      <ActionSectionForm
        target={{
          id: '',
          name: destination.name,
          actions: destinationType.actions || [],
          configurations: [],
        }}
        action={action}
        userIsAdmin={currentMemberIsAdmin}
        formData={dataFor(action)}
        errors={rsjfErrors[action.id]}
        onChange={(data: any, fieldId?: string) =>
          onChangeJSONForm(data, action.id, fieldId)
        }
        showSetupState={false}
        // The switch is drawn on the section heading's line instead.
        renderScheduleToggle={false}
        // Drawn in the section heading instead, beside the on/off switch.
        showDescription={false}
        // Creation flows have no integration id yet, so ReferenceSelectWidget
        // reaches its live options through draft mode — which it refuses to
        // enter without a token, falling back to a plain text input. Dropping
        // this prop costs the user every reference dropdown on the screen.
        token={authToken}
        draftReference={draftReference}
        headerActions={
          action.schema?.is_executable === true ? (
            <DraftTestActionButton
              action={action}
              providerType={destinationType}
              draftReference={draftReference}
              authToken={authToken}
              cannotTestHint={t('Save the Connection to test credentials')}
              testid="DestinationTestActionHeader-button"
            />
          ) : undefined
        }
      />
    );

  // Only the technologies Gundi reaches at a supplied address get this field.
  const urlRow = (
    <div className="mb-[8px] w-full border-b border-ui-divider-lines pb-[8px] pt-1 transition-all duration-300 last:mb-0 last:border-b-0 last:pb-0">
      <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="flex flex-col md:w-1/2">
          <label className="mr-1">
            {isEarthRanger ? t('Site URL') : t('URL')}
            <RequiredTag />
          </label>
          <span className="mt-1 text-sm text-ui-text-primary-text">
            {isEarthRanger
              ? t('The name of the EarthRanger site (e.g. my-site).')
              : t('The base URL of this Connection.')}
          </span>
        </div>
        <div className="md:w-1/2">
          {isEarthRanger ? (
            <EarthRangerSiteInput
              value={destination.base_url || ''}
              onChange={(e) => onChange(e)}
              error={errors.base_url}
            />
          ) : (
            <>
              <input
                id="base_url"
                placeholder=""
                className="w-full rounded rounded-sm border border-unlisted-neutral-gray-200 p-1.5 px-2 transition-colors duration-300"
                value={destination.base_url || ''}
                onChange={(e) => onChange(e)}
              ></input>
              {errors.base_url && (
                <div className="flex flex-row items-center rounded py-1 text-unlisted-orange-600">
                  <AttentionIcon />
                  <p className="ml-1 mt-0.5">{errors.base_url}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const detailsSection = (
    <div className="rounded border border-unlisted-gray-lines bg-white p-5 transition-colors duration-300">
      {/* Name */}
      <div className="mb-[8px] w-full border-b border-ui-divider-lines pb-[8px] pt-1 transition-all duration-300">
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex flex-col md:w-1/2">
            <label className="mr-1">
              {t('Name')}
              <RequiredTag />
            </label>
            <span className="mt-1 text-sm text-ui-text-primary-text">
              {t('A descriptive name for this Connection.')}
            </span>
          </div>
          <div className="md:w-1/2">
            <input
              id="name"
              placeholder=""
              className="w-full rounded rounded-sm border border-unlisted-neutral-gray-200 p-1.5 px-2 transition-colors duration-300"
              value={destination.name || ''}
              onChange={(e) => onChange(e)}
            ></input>
            {errors.name && (
              <div className="flex flex-row items-center rounded py-1 text-unlisted-orange-600">
                <AttentionIcon />
                <p className="ml-1 mt-0.5">{errors.name}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Workspace */}
      <div className="mb-[8px] w-full border-b border-ui-divider-lines pb-[8px] pt-1 transition-all duration-300 last:mb-0 last:border-b-0 last:pb-0">
        <div className="flex w-full flex-col gap-2 md:flex-row md:items-start md:justify-between md:gap-6">
          <div className="flex flex-col md:w-1/2">
            <label className="mr-1">
              {t('Workspace')}
              <RequiredTag />
            </label>
            <span className="mt-1 text-sm text-ui-text-primary-text">
              {t('The workspace this Connection belongs to.')}
            </span>
          </div>
          <div className="md:w-1/2">
            <SelectInput
              options={organizations.map((org: Organization) => ({
                label: org.name,
                value: org.id,
              }))}
              className={'basic-single text-ui-text-primary-text'}
              autoSelectSingleOption={!orgSearchActive}
              isDisabled={false}
              isLoading={false}
              isClearable={false}
              isSearchable={true}
              isMulti={false}
              name={t('Workspace')}
              onChange={(e: any) => onChangeOrg(e)}
              onMenuScrollToBottom={loadMoreOrgs}
              handleInputChange={orgInputChange}
            />
            {errors.owner && (
              <div className="flex flex-row items-center rounded py-1 text-unlisted-orange-600">
                <AttentionIcon />
                <p className="ml-1 mt-0.5">{errors.owner}</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {urlIsRequired && urlRow}
    </div>
  );

  return (
    <div className="z-20">
      <div className="w-full">
        <div>
          <div>
            {/* The chosen technology rides on the title line, opposite
                            the heading — it identifies what is being created rather
                            than belonging to the configuration below it. */}
            <div
              className={`${wizardClasses.band} flex-wrap justify-between gap-3`}
            >
              <div
                className={`${wizardClasses.row} flex flex-wrap items-center justify-between gap-3`}
              >
                <h1 className={wizardClasses.title}>
                  {receiveOnly ? t('New Destination') : t('New Connection')}
                </h1>
                {typeSelected && (
                  <div className="flex items-center gap-3">
                    <TechnologyIcon
                      typeValue={destinationType.value}
                      typeName={destinationType.name}
                      boxClassName="w-[31px] h-[31px] text-base"
                      imgClassName="w-[38px] h-[38px] object-contain"
                    />
                    <span className="font-medium text-ui-text-primary-text transition-colors duration-300">
                      {destinationType.name}
                    </span>
                    <button
                      type="button"
                      data-testid="DestinationModal-change-type-button"
                      className="text-sm text-green-3 underline"
                      onClick={onClearType}
                    >
                      {t('Change')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className={wizardClasses.body}>
              <div className="mx-auto flex w-full max-w-list-table flex-row justify-center">
                <div className="w-full pb-8">
                  {/* STEP 1: TECHNOLOGY — the same card grid the
                                        Route wizard opens with. Pick a technology
                                        first; its configuration replaces the grid,
                                        and the choice moves up to the title line. */}
                  {!typeSelected && (
                    <div className="px-4 sm:px-6">
                      <div className="mb-5 flex flex-col">
                        {/* A heading, not a <label>: it names the
                                                    step rather than any one control, and it
                                                    sits under the page's h1. */}
                        <h2
                          className={
                            textClasses.subheading +
                            ' w-full text-center !text-[26px]'
                          }
                        >
                          {t('What would you like to connect?')}
                        </h2>
                        <TechnologySearchInput
                          id="type-search"
                          testId="DestinationModal-type-search-input"
                          value={typeSearch}
                          placeholder={t('Search technologies')}
                          ariaLabel={t('Search technologies')}
                          onChange={setTypeSearch}
                        />
                      </div>
                      {!typesLoaded ? (
                        <TechnologyCardGrid
                          items={[]}
                          loading={true}
                          selectedId={null}
                          onSelect={() => {}}
                          testIdPrefix="DestinationModal-technology"
                        />
                      ) : typesError ? (
                        // A failed fetch is not an over-narrow
                        // search: telling the user to adjust their
                        // filters would send them nowhere.
                        // Wrapped like TechnologyCardGrid's own empty
                        // state, so the message reads as a card on the
                        // page tint rather than floating on it.
                        <div className="rounded-lg border border-teal-t30 bg-white py-8">
                          <NoResults
                            headerMsg={t('Could not load technologies')}
                            labelMsg={t(
                              'Please try again or contact system admin.',
                            )}
                          />
                        </div>
                      ) : gridGroups.length === 0 ? (
                        <div className="rounded-lg border border-teal-t30 bg-white py-8">
                          <NoResults
                            headerMsg={t('No Results Found')}
                            labelMsg={t(
                              'You may need to adjust your search or filters to find what you are looking for',
                            )}
                          />
                        </div>
                      ) : (
                        gridGroups.map((group, i) => (
                          <div key={group.heading ?? i} className="mb-8">
                            {group.heading && (
                              <h3 className="mb-3 text-lg font-semibold text-ui-text-primary-text transition-colors duration-300">
                                {group.heading}
                              </h3>
                            )}
                            <TechnologyCardGrid
                              items={group.items}
                              loading={false}
                              selectedId={pendingTypeId}
                              onSelect={(item) => setPendingTypeId(item.id)}
                              testIdPrefix="DestinationModal-technology"
                            />
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  {/* STEP 2: NAME, CONFIGURATION, ENABLED */}
                  {/* Same shell as a Route's configuration
                                        panel: nav on the left, one section at a time.
                                        Everything here is committed by one submit —
                                        the destination doesn't exist yet, so there is
                                        nothing to save section by section. */}
                  {typeSelected && (
                    <div className="mb-8 mt-8 px-4 sm:px-6">
                      <SelectionPanel
                        title={t('Configuration')}
                        nav={nav}
                        onSelectSection={setActiveSlug}
                        stackedSections={nav.flatMap((section) =>
                          section.items.map((item) => ({
                            slug: item.slug,
                            label: item.label,
                            description:
                              item.slug === DETAILS_SLUG
                                ? undefined
                                : actionDescription(
                                    actionForSlug(actions, item.slug),
                                  ) || undefined,
                            headerRight:
                              item.slug === DETAILS_SLUG
                                ? undefined
                                : (() => {
                                    const action = actionForSlug(
                                      actions,
                                      item.slug,
                                    );
                                    return action ? (
                                      <ScheduleToggle
                                        action={action}
                                        data={dataFor(action)}
                                        onChange={(
                                          data: any,
                                          fieldId: string,
                                        ) =>
                                          onChangeJSONForm(
                                            data,
                                            action.id,
                                            fieldId,
                                          )
                                        }
                                      />
                                    ) : undefined;
                                  })(),
                            content:
                              item.slug === DETAILS_SLUG
                                ? detailsSection
                                : actionSection(
                                    actionForSlug(actions, item.slug),
                                  ),
                          })),
                        )}
                      >
                        {null}
                      </SelectionPanel>
                    </div>
                  )}
                </div>
              </div>
              {/* Submit and cancel, parked at the bottom of the window
                                as the Route wizard's are. Sticky inside a full-height
                                column leaves empty tint below them on a short form. */}
              <div className={wizardClasses.footer}>
                <div className="px-4 md:px-8 desktop:px-[60px]">
                  <div
                    className={`${wizardClasses.row} flex flex-row items-center gap-3 px-4 sm:px-6`}
                  >
                    {typeSelected ? (
                      <button
                        className={buttonClasses.primary}
                        type="submit"
                        disabled={!changesMade}
                        aria-disabled={!changesMade}
                        onClick={(e: any) => onSave(e, 'save')}
                      >
                        {t('Create Connection')}
                      </button>
                    ) : (
                      <button
                        className={buttonClasses.primary}
                        type="button"
                        data-testid="DestinationModal-next-button"
                        disabled={!pendingTypeId}
                        aria-disabled={!pendingTypeId}
                        onClick={confirmPendingType}
                      >
                        {t('Next')}
                      </button>
                    )}
                    <button
                      className={buttonClasses.cancel}
                      type="button"
                      onClick={onToggleModal}
                    >
                      {t('Cancel')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationModal;
